import { useMemo, useState } from "react";
import { NavLink, useLocation } from "@/lib/router";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, Plus } from "lucide-react";
import { usePractice } from "../context/PracticeContext";
import { useDialog } from "../context/DialogContext";
import { useSidebar } from "../context/SidebarContext";
import { agentsApi } from "../api/agents";
import { heartbeatsApi } from "../api/heartbeats";
import { queryKeys } from "../lib/queryKeys";
import { cn, agentRouteRef, agentUrl } from "../lib/utils";
import { AgentIcon } from "./AgentIconPicker";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import type { Agent } from "@ligament/shared";

function sortByName(agents: Agent[]): Agent[] {
  return [...agents].sort((a, b) => a.name.localeCompare(b.name));
}

function isPlatformAgent(agent: Agent): boolean {
  const meta = agent.metadata as Record<string, unknown> | null | undefined;
  return meta?.isPlatformAgent === true;
}

const PATIENT_JOURNEY_FOLDER_KEYS = new Set([
  "patient-journey-monitor",
  "stage-2-initial",
  "stage-3-ultrasound",
  "stage-5-procedure",
]);

function isPatientJourneyAgent(agent: Agent): boolean {
  const meta = agent.metadata as Record<string, unknown> | null | undefined;
  const folderKey = meta?.folderKey as string | undefined;
  return !!folderKey && PATIENT_JOURNEY_FOLDER_KEYS.has(folderKey);
}

function isPatientJourneyMonitor(agent: Agent): boolean {
  const meta = agent.metadata as Record<string, unknown> | null | undefined;
  return (meta?.folderKey as string | undefined) === "patient-journey-monitor";
}

interface AgentRowProps {
  agent: Agent;
  liveCount: number;
  indent?: boolean;
  onClick?: () => void;
  activeAgentId: string | null;
}

function AgentRow({ agent, liveCount, indent = false, onClick, activeAgentId }: AgentRowProps) {
  const dest = agentUrl(agent);
  const isActive = activeAgentId === agentRouteRef(agent);
  return (
    <NavLink
      to={dest}
      onClick={onClick}
      className={cn(
        "flex items-center gap-2.5 py-1.5 text-[13px] font-medium transition-colors",
        indent ? "pl-7 pr-3" : "px-3",
        isActive
          ? "bg-accent text-foreground"
          : "text-foreground/80 hover:bg-accent/50 hover:text-foreground"
      )}
    >
      <AgentIcon icon={agent.icon} className="shrink-0 h-3.5 w-3.5 text-muted-foreground" />
      <span className="flex-1 truncate">{agent.name}</span>
      {liveCount > 0 && (
        <span className="ml-auto flex items-center gap-1.5 shrink-0">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500" />
          </span>
          <span className="text-[11px] font-medium text-blue-600 dark:text-blue-400">
            {liveCount}
          </span>
        </span>
      )}
    </NavLink>
  );
}

interface GroupProps {
  label: string;
  defaultOpen?: boolean;
  agents: Agent[];
  liveCountByAgent: Map<string, number>;
  activeAgentId: string | null;
  onNav: () => void;
  children?: React.ReactNode;
}

function AgentGroup({ label, defaultOpen = true, agents, liveCountByAgent, activeAgentId, onNav, children }: GroupProps) {
  const [open, setOpen] = useState(defaultOpen);
  const hasLive = agents.some(a => (liveCountByAgent.get(a.id) ?? 0) > 0);

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger className={cn(
        "flex items-center gap-2 w-full px-3 py-1.5 transition-colors hover:bg-accent/50",
        open ? "text-foreground/70" : "text-muted-foreground"
      )}>
        {hasLive && (
          <span className="relative flex h-1.5 w-1.5 shrink-0">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-blue-500" />
          </span>
        )}
        <span className="flex-1 text-left text-[10px] font-semibold uppercase tracking-widest">{label}</span>
        <ChevronDown className={cn("h-3 w-3 text-muted-foreground/50 transition-transform shrink-0", !open && "-rotate-90")} />
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="flex flex-col gap-0.5 mb-0.5">
          {children}
          {agents.map(agent => (
            <AgentRow
              key={agent.id}
              agent={agent}
              liveCount={liveCountByAgent.get(agent.id) ?? 0}
              indent={!!children}
              onClick={onNav}
              activeAgentId={activeAgentId}
            />
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

export function SidebarAgents() {
  const [open, setOpen] = useState(true);
  const { selectedPracticeId } = usePractice();
  const { openNewAgent } = useDialog();
  const { isMobile, setSidebarOpen } = useSidebar();
  const location = useLocation();

  const { data: agents } = useQuery({
    queryKey: queryKeys.agents.list(selectedPracticeId!),
    queryFn: () => agentsApi.list(selectedPracticeId!),
    enabled: !!selectedPracticeId,
  });

  const { data: liveRuns } = useQuery({
    queryKey: queryKeys.liveRuns(selectedPracticeId!),
    queryFn: () => heartbeatsApi.liveRunsForCompany(selectedPracticeId!),
    enabled: !!selectedPracticeId,
    refetchInterval: 10_000,
  });

  const liveCountByAgent = useMemo(() => {
    const counts = new Map<string, number>();
    for (const run of liveRuns ?? []) {
      counts.set(run.agentId, (counts.get(run.agentId) ?? 0) + 1);
    }
    return counts;
  }, [liveRuns]);

  const { pjMonitor, pjStages, platformAgents, otherAgents } = useMemo(() => {
    const visible = (agents ?? []).filter((a: Agent) => a.status !== "disabled");
    const pjMonitor = visible.find(isPatientJourneyMonitor) ?? null;
    const pjStages = sortByName(visible.filter(a => isPatientJourneyAgent(a) && !isPatientJourneyMonitor(a)));
    const platformAgents = sortByName(visible.filter(isPlatformAgent));
    const otherAgents = sortByName(visible.filter(a => !isPatientJourneyAgent(a) && !isPlatformAgent(a)));
    return { pjMonitor, pjStages, platformAgents, otherAgents };
  }, [agents]);

  const agentMatch = location.pathname.match(/^\/agents\/([^/]+)/);
  const activeAgentId = agentMatch?.[1] ?? null;

  function handleNav() {
    if (isMobile) setSidebarOpen(false);
  }

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div className="flex items-center px-3 py-2">
        <CollapsibleTrigger className="flex items-center justify-between flex-1 min-w-0 cursor-pointer">
          <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/70">
            Agents
          </span>
          <ChevronDown className={cn("h-3 w-3 text-muted-foreground/50 transition-transform", !open && "-rotate-90")} />
        </CollapsibleTrigger>
        <button
          onClick={(e) => { e.stopPropagation(); openNewAgent(); }}
          className="flex items-center justify-center h-4 w-4 rounded text-muted-foreground/50 hover:text-foreground hover:bg-accent/50 transition-colors ml-1"
          aria-label="New agent"
        >
          <Plus className="h-3 w-3" />
        </button>
      </div>

      <CollapsibleContent>
        <div className="flex flex-col gap-0.5 mt-0.5">

          {(pjMonitor || pjStages.length > 0) && (
            <AgentGroup
              label="Patient Journey"
              defaultOpen={true}
              agents={pjStages}
              liveCountByAgent={liveCountByAgent}
              activeAgentId={activeAgentId}
              onNav={handleNav}
            >
              {pjMonitor && (
                <AgentRow
                  agent={pjMonitor}
                  liveCount={liveCountByAgent.get(pjMonitor.id) ?? 0}
                  indent={false}
                  onClick={handleNav}
                  activeAgentId={activeAgentId}
                />
              )}
            </AgentGroup>
          )}

          {platformAgents.length > 0 && (
            <AgentGroup
              label="Platform"
              defaultOpen={true}
              agents={platformAgents}
              liveCountByAgent={liveCountByAgent}
              activeAgentId={activeAgentId}
              onNav={handleNav}
            />
          )}

          {otherAgents.length > 0 && (
            <AgentGroup
              label="Other"
              defaultOpen={false}
              agents={otherAgents}
              liveCountByAgent={liveCountByAgent}
              activeAgentId={activeAgentId}
              onNav={handleNav}
            />
          )}

        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
